/**
 * firstPartyCallback — the /integrations/callback page's part in an app Prest0n connects through its OWN OAuth
 * client (firstPartyApps.ts — Upwork, docs/PLAN-upwork-integration-20261009.md PR B1).
 *
 * Upwork sends the person back to https://prest0.ai/integrations/callback?code=…&state=upw_…. The page tells that
 * return from a Composio one by the state's prefix (the server mints every Upwork state with it:
 * functions/src/upwork/upworkOAuth.ts UPWORK_STATE_PREFIX, pinned equal by a test), takes the code out of the
 * address bar at once, waits for the person's sign-in to load, and posts both to the server, which completes the
 * connection only for the person who started it. The window the person started from sees the connection through
 * its usual poll.
 *
 * One post per state, however often the page mounts (React's development double effect included): the server
 * accepts a state once, so a second post would turn a success into a refusal.
 */
import { getAuth } from 'firebase/auth';
import { completeFirstPartyConnect } from './ComposioClient';

/** Twin of the server's UPWORK_STATE_PREFIX (functions/src/upwork/upworkOAuth.ts), pinned equal by a test. */
export const UPWORK_STATE_PREFIX = 'upw_';

export interface FirstPartyReturn {
  toolkit: 'upwork';
  state: string;
  code: string | null;
  /** The app's own `error` (the person declined, or the app refused): nothing to post. */
  error: string | null;
}

/** Pure: is this page load an Upwork sign-in coming back? */
export function firstPartyReturnOf(pathname: string, search: string): FirstPartyReturn | null {
  if (pathname !== '/integrations/callback') return null;
  const params = new URLSearchParams(search);
  const state = params.get('state') || '';
  if (!state.startsWith(UPWORK_STATE_PREFIX) || state.length > 80) return null;
  return { toolkit: 'upwork', state, code: params.get('code'), error: params.get('error') };
}

const AUTH_WAIT_MS = 10_000;
// The server exchanges the code (≤ 15 s) and records the tool list (≤ 40 s) before it answers.
const POST_WAIT_MS = 90_000;

function withinMs<T>(p: Promise<T>, ms: number, fallback: T): Promise<T> {
  return new Promise((resolve) => {
    const t = setTimeout(() => resolve(fallback), ms);
    p.then((v) => { clearTimeout(t); resolve(v); }, () => { clearTimeout(t); resolve(fallback); });
  });
}

export interface FirstPartyCallbackDeps {
  /** Resolves once the signed-in person (if any) is known. */
  authReady: () => Promise<boolean>;
  complete: (toolkit: string, state: string, code: string) => Promise<unknown>;
}

const defaultDeps: FirstPartyCallbackDeps = {
  authReady: async () => {
    const auth = getAuth();
    await auth.authStateReady();
    return Boolean(auth.currentUser);
  },
  complete: completeFirstPartyConnect,
};

const posts = new Map<string, Promise<boolean>>();

/** Completes the connection once per state. True only when the server said it is connected. */
export function completeFirstPartyReturn(ret: FirstPartyReturn, deps: FirstPartyCallbackDeps = defaultDeps): Promise<boolean> {
  const running = posts.get(ret.state);
  if (running) return running;
  const p = (async () => {
    if (ret.error || !ret.code) return false;
    if (!(await withinMs(deps.authReady(), AUTH_WAIT_MS, false))) return false;
    return withinMs(deps.complete(ret.toolkit, ret.state, ret.code).then(() => true), POST_WAIT_MS, false);
  })();
  posts.set(ret.state, p);
  return p;
}

/** Test hook. */
export function resetFirstPartyCallbackForTests(): void {
  posts.clear();
}
